/**
 * One template in the master list: its cover, its name and span, the system it runs on, and how
 * many clients follow it.
 *
 * ── The whole row is a LINK, and pointing at it is a different act ───────────────────────────
 * Owner decisions D8 and D9, 3 September 2026: the pointer SELECTS — the inspector beside the list
 * swaps to whatever the trainer is over, and the selection stays on the last row pointed at — while
 * a CLICK opens the plan in the builder. The inspector is read-only, so the row is the way out of
 * it.
 *
 * ── `mousemove`, NOT `mouseenter`, and that is a measured decision rather than a preference ───
 * A dialog makes the rest of the page `inert`, so no row can be hovered while one is open — and the
 * instant it closes the browser re-evaluates hover and fires `mouseenter` on whatever row happens
 * to sit under a pointer that never moved. On the clients roster that swapped the panel to a client
 * the trainer had not chosen, unmounted the very control Base UI was about to hand focus back to,
 * and dropped focus to `<body>`. It is pinned there by `clients-panel.spec.ts`'s focus-return case,
 * and the same canon governs this screen.
 *
 * `mousemove` says what the canon's `mouseenter` meant: the trainer moved the pointer here. A
 * stationary pointer fires none, and a repeat on the row already selected sets the same key, which
 * React discards without a render.
 *
 * **Focus into the row selects it too**, through `onFocusCapture` — focus does not bubble, and the
 * anchor inside the row is where it lands. That is the whole keyboard path: the arrows move focus
 * (see `ListRows`), focus moves the selection, and Enter follows the anchor that already has it. No
 * second set of controls, and no `aria-selected` on a row that is not in a selectable widget.
 */

import { Link } from '@tanstack/react-router';

import photoBox from '~/shared/ui/photoBox.module.css';
import type { PhotoReveal } from '~/shared/lib/usePhotoReveal';

import type { PlanLibraryRow } from '../model/planLibraryRow';
import { CalendarModeIcon, PersonIcon, SelfPacedModeIcon } from './PlanLibraryIcons';
import listRow from './listRow.module.css';
import styles from './TemplateRow.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

interface TemplateRowProps {
  /** The list's key for this row, which is also its arrow-navigation handle. See `ListRows`. */
  readonly rowKey: string;
  readonly row: PlanLibraryRow;
  /**
   * The sub-line: how long the plan runs, and nothing else — owner decision D26, 4 September 2026,
   * which took the exercise count out of it and left it to the inspector hero's first KPI. `null`
   * for a plan with no phases, which has no span to state; the name then stands on its own.
   */
  readonly subline: string | null;
  /**
   * `Kategoria: Siła`, or `null` where the plan carries none. Composed by `ListRows`, like every
   * other sentence here.
   */
  readonly categorySpoken: string | null;
  readonly modeLabel: string;
  /** The figure, or the word for nobody. */
  readonly clientsLabel: string;
  /** The same fact as a sentence, because a bare figure beside a drawing has no unit in speech. */
  readonly clientsSpoken: string;
  readonly selected: boolean;
  readonly onPoint: () => void;
  /**
   * The list's shared photograph group — every cover in it appears on one frame rather than as its
   * own file lands. Owned by `ListRows`, because the group is the list; see `usePhotoReveal`.
   */
  readonly photo: PhotoReveal;
}

export function TemplateRow({
  rowKey,
  row,
  photo,
  subline,
  categorySpoken,
  modeLabel,
  clientsLabel,
  clientsSpoken,
  selected,
  onPoint,
}: TemplateRowProps) {
  const idle = row.assignedClientCount === 0;
  const ModeIcon = row.schedulingMarker === 'selfPaced' ? SelfPacedModeIcon : CalendarModeIcon;

  return (
    <li
      className={`${String(listRow.row)} ${String(styles.row)} ${
        selected ? String(listRow.rowSelected) : ''
      }`}
      // `undefined` on the row the pane is ALREADY describing: a pointer resting on a row fires
      // `mousemove` sixty to a hundred and twenty times a second, and every one of those was a
      // round of no-op work to select what was already selected.
      //
      // Still `mousemove` and NOT `mouseenter`. A dialog makes the rest of the page `inert`, so no
      // row is hoverable while one is open, and the instant it closes the browser re-evaluates
      // hover and fires `mouseenter` under a pointer that never moved — on the roster that swapped
      // the panel to a client nobody chose and dropped focus to `<body>`.
      onMouseMove={selected ? undefined : onPoint}
      onFocusCapture={onPoint}
    >
      {selected && <span className={listRow.selectionBar} aria-hidden />}

      {/* A photograph and nothing else. The category's two colour locals used to be set here for
        * the stripe across the cover's foot; that stripe went on 17 September 2026 and nothing
        * under this element reads them any more. */}
      <span className={listRow.cover}>
        <img
          className={`${String(photoBox.photo)} ${String(listRow.coverImage)} ${photo.holding} ${photo.reveal}`}
          src={row.thumbUrl}
          alt=""
          // A library can run past a screenful of rows and every cover below the first few is a
          // 51px thumbnail. The box is already reserved, so deferring the fetch moves nothing.
          loading="lazy"
          decoding="async"
          // A failure is an arrival: a cover that 404s has to open the group's window like any
          // other, or one broken file would hold a whole screenful grey.
          onLoad={photo.onPhotoSettled}
          onError={photo.onPhotoSettled}
        />
      </span>

      <span className={listRow.identity}>
        <span className={listRow.nameLine}>
          <Link
            className={listRow.rowLink}
            to="/plans/$planId"
            params={{ planId: String(row.id) }}
            data-plan-row={rowKey}
            // The full name stays reachable under a pointer and in the accessibility tree when the
            // flexible track clips it.
            title={row.name}
            // The category is drawn as a 2.5px rule under the cover and nowhere else, so a reader
            // user meets a facet in the filter panel they cannot hear on a row. An `aria-label`
            // rather than an off-screen tail inside the anchor: this app measures itself against
            // its canon by pairing on DRAWN TEXT, and a hidden span inside a row would put a
            // sentence into the row's text that the specimen has no element for. The visible name
            // is the first thing in the name either way, which is what 2.5.3 requires.
            aria-label={categorySpoken === null ? undefined : `${row.name}, ${categorySpoken}`}
          >
            {row.name}
          </Link>
        </span>
        <span className={listRow.sub}>{subline}</span>
      </span>

      <span className={listRow.mode}>
        <ModeIcon className={listRow.modeIcon} />
        <span className={listRow.modeLabel}>{modeLabel}</span>
      </span>

      {/* The mark and the figure are one fact drawn twice, so both are hidden and one sentence is
        * read in their place: "7" beside a drawing of a person is a number with no unit in speech,
        * and the drawing is the only thing supplying it. */}
      <span className={styles.clients}>
        <PersonIcon className={`${styles.clientsIcon} ${idle ? styles.clientsIconIdle : ''}`} />
        <span
          className={`${styles.clientsFigure} ${idle ? styles.clientsFigureIdle : ''}`}
          aria-hidden
        >
          {clientsLabel}
        </span>
        <span className={hidden.srOnly}>{clientsSpoken}</span>
      </span>
    </li>
  );
}
